/**
 * What the order desk contributes to the route tree.
 *
 * Each route declares the permission it needs, and the same declaration drives
 * both the router's gate and the navigation entry — so a menu item cannot
 * appear for a page the user would be refused.
 *
 * The list and the detail are `orders.order.read`, which the ADV, the
 * warehouse and the sales representative all hold; what each of them can *do*
 * on the detail screen is decided by the two write permissions, which the
 * screen reads itself. The settings page is its own permission, because the
 * VAT rate applies to every future order.
 */

import { useParams } from "@tanstack/react-router";
import type { ApiClient } from "@hardauto/core-api";
import type { FeatureRoutes } from "@hardauto/core-router";

import { OrderScreen } from "./screens/OrderScreen";
import { OrdersScreen } from "./screens/OrdersScreen";
import { PolicyScreen } from "./screens/PolicyScreen";
import { ORDERS_PATHS } from "./paths";
import "./i18n";

export interface OrdersRoutesOptions {
  /**
   * The customer the back office's context is on, if any. Injected by the
   * application, which owns the context; the lists narrow to that customer.
   */
  useClient?: () => { id: string; label: string } | null;
}

const noClient = () => null;

export function ordersRoutes(client: ApiClient, options: OrdersRoutesOptions = {}): FeatureRoutes {
  // Chosen once: a hook must be the same hook on every render.
  const useClient = options.useClient ?? noClient;

  function Orders() {
    return <OrdersScreen client={client} fixedKind="order" scopeClient={useClient()} />;
  }

  function Quotes() {
    return <OrdersScreen client={client} fixedKind="quote" scopeClient={useClient()} />;
  }

  function OneOrder() {
    // `strict: false` because the route tree is assembled at runtime from a
    // registry, so TanStack cannot infer per-route parameter types.
    const params: { id?: string } = useParams({ strict: false });
    return <OrderScreen client={client} id={params.id ?? ""} />;
  }

  function Policy() {
    return <PolicyScreen client={client} />;
  }

  return {
    id: "orders",
    routes: [
      {
        path: ORDERS_PATHS.orders,
        component: Orders,
        layout: "app",
        requirement: { authenticated: true, permissions: ["orders.order.read"] },
        nav: {
          group: "orders:group",
          labelKey: "orders:nav.orders",
          section: "main",
          order: 40,
        },
      },
      {
        path: ORDERS_PATHS.quotes,
        component: Quotes,
        layout: "app",
        requirement: { authenticated: true, permissions: ["orders.order.read"] },
        nav: {
          group: "orders:group",
          labelKey: "orders:nav.quotes",
          section: "main",
          order: 39,
        },
      },
      {
        // Declared BEFORE the parameterised route: `/commandes/parametres`
        // would otherwise be read as an order whose id is "parametres", and
        // the screen would report "commande introuvable".
        path: ORDERS_PATHS.policy,
        component: Policy,
        layout: "app",
        requirement: { authenticated: true, permissions: ["orders.policy.read"] },
        nav: {
          group: "orders:group",
          labelKey: "orders:nav.policy",
          section: "main",
          order: 41,
        },
      },
      {
        path: ORDERS_PATHS.order,
        component: OneOrder,
        layout: "app",
        requirement: { authenticated: true, permissions: ["orders.order.read"] },
      },
    ],
  };
}
